import React, { useEffect, useState } from 'react';
import { useParams, useHistory, useLocation, matchPath } from 'react-router-dom';
import { CNav, CNavItem, CNavLink, CCol, CButton, CInput, CTooltip, CDropdownMenu, CDropdownItem, CDropdownToggle, CDropdown } from '@coreui/react';
import { GetProjectInformation, GetProjectTabStatus, UpdateProjectInfo, GetTabWarnings } from 'src/request/Projects';
import '../projectdetails/ProjectDetails.css';
import { FaPencilAlt, FaArrowCircleLeft, FaExclamationCircle } from 'react-icons/fa';
import { OverviewWarning } from 'src/request/OverView';
import { useDispatch, useSelector } from 'react-redux';
import { setProjectInfo, setProjectTab, setTabWarnings } from 'src/store/feature/project/projectSlice';
import { setModule } from 'src/store/feature/module/moduleSlice';
import { trackEvent } from 'src/utils/mixpanel';
const Swal = require('sweetalert2');

const ProjectTabs = () => {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const [isEditInfo, setIsEditInfo] = useState(false);
  const [editableInfo, setEditableInfo] = useState(null);
  const projectInfo = useSelector((state) => state.project.projectInfo[id]);
  const tabStatus = useSelector((state) => state.project.projectTab[id]);
  const tabWarnings = useSelector((state) => state.project.tabWarnings[id]);
  const refetchTrigger = useSelector((state) => state.project.refetchTrigger[id]);
  const allowedPermission = useSelector(state => state.auth.accessPermissions);
  const [notFound, setNotFound] = useState(false);
  const tabs = [
    { label: "Overview", path: `/project/${id}/overview`, key: 'overview', recommenderKey: 'project-overview' },
    { label: "Estimate", path: `/project/${id}/estimate`, key: 'estimate', recommenderKey: 'project-estimate' },
    { label: "Scope of Work", path: `/project/${id}/sow`, key: 'scope_of_work', recommenderKey: 'project-scope-of-work' },
    { label: "Report", path: `/project/${id}/report`, key: 'report', recommenderKey: 'project-reports' },
    { label: "Materials", path: `/project/${id}/material`, key: 'materials', recommenderKey: 'project-materials' },
    { label: "Equipment", path: `/project/${id}/equipment`, key: 'equipment', recommenderKey: 'project-equipment' },
    { label: "Team", path: `/project/${id}/team`, key: 'team', recommenderKey: 'project-team' },
    { label: "Updates", path: `/project/${id}/update`, key: 'notes', recommenderKey: 'project-updates' },
  ];

  const normalizedPermissions = allowedPermission
    .filter(p => p.path)
    .map(p => p.path.replace(/<([^>]+)>/g, ':$1').toLowerCase());

  const accessibleTabs = tabs.filter(tabsPath =>
    normalizedPermissions.some(permPath =>
      matchPath(tabsPath.path, { path: permPath, exact: true })
    ))

  const fetchProjectInformation = async () => {
    try {
      const response = await GetProjectInformation(id);
      if (response?.data?.data) {
        const data = {
          project_id: response?.data?.data?.project_id,
          project_name: response?.data?.data?.project_name,
          project_identifier: response?.data.data.project_identifier,
          start_date: response?.data?.data?.start_date,
          end_date: response?.data?.data?.end_date,
          estimate_status: response?.data?.data?.estimate_status,
          project_category: response?.data?.data?.project_category,
          project_total_budget: response?.data?.data?.project_total_budget,
          project_total_spent: response?.data?.data?.project_total_spent,
          project_type_id: response?.data?.data?.project_type_id,
        };
        dispatch(setProjectInfo({ projectId: id, data }));
      }
    } catch (error) {
      console.error('Failed to fetch project information', error);
      setNotFound(true);
    }
  };

  const fetchTabStatus = async () => {
    try {
      const response = await GetProjectTabStatus(id);
      if (response?.status === 200) {
        dispatch(setProjectTab({ projectId: id, tabData: response.data.response }));
      }
    } catch (error) {
      console.error('Something went wrong please try again.', error);
    }
  };

  const fetchProjectTabWarning = async () => {
    try {
      const [overviewRes, tabRes] = await Promise.all([OverviewWarning(id), GetTabWarnings(id)]);
      const data = {
        overview: overviewRes?.data?.status === 200 ? overviewRes?.data?.results?.[0]?.warning : null,
        report: tabRes?.data?.status === 200 ? tabRes?.data?.response?.Report?.warning : null,
        estimate: tabRes?.data?.status === 200 ? tabRes?.data?.response?.Estimate?.warning : null,
        historical_report: tabRes?.data?.status === 200 ? tabRes?.data?.response?.Report?.count?.historical_report : null,
      };
      dispatch(setTabWarnings({ projectId: id, data }));
    } catch (error) {
      console.error('Failed to fetch project warnings', error);
    }
  };

  const saveProjectInfo = async () => {
    const user_id = localStorage.getItem('user_id') ?? null;
    const payload = {
      name: editableInfo.project_name,
      identifier: editableInfo.project_identifier,
      user_id,
    };
    try {
      const response = await UpdateProjectInfo(editableInfo.project_id, payload);
      if (response.status === 200) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Successfully updated project information',
          showConfirmButton: true,
          timer: 1500,
        });
        dispatch(setProjectInfo({ projectId: id, data: editableInfo }));
        setIsEditInfo(false);
      }
    } catch (error) {
      console.error('Something went wrong please try again later', error);
    }
  };



  useEffect(() => {
    fetchProjectInformation();
    fetchTabStatus();
    fetchProjectTabWarning();
  }, [id, refetchTrigger]);

  useEffect(() => {
    if (notFound) {
      history.replace('/403');
    }
  }, [notFound, history]);

  const ScopeOfWorkWarningIcon = () => {
    return (
      <>
        <CTooltip content="Some POW amounts are missing. Please input the missing POW amounts">
          <div className="relative flex items-center justify-center w-6 h-6">
            <span className="absolute inline-flex w-full h-full rounded-full bg-[#B42A19]/30 animate-ping"></span>
            <FaExclamationCircle className="relative z-10 flex items-center justify-center w-6 h-6 text-[#B42A19] bg-white rounded-full ring-2 ring-[#B42A19]/40 transition-transform duration-700 ease-in-out animate-[pulse-scale_1.6s_ease-in-out_infinite" />
          </div>
        </CTooltip>
      </>
    )
  }

  const EstimateWarningIcon = () => {
    return (
      <>
        <CTooltip content="It looks like you haven’t uploaded material, labor, or equipment yet. Please upload them first to continue.">
          <div className="relative flex items-center justify-center w-6 h-6">
            <span className="absolute inline-flex w-full h-full rounded-full bg-[#B42A19]/30 animate-ping"></span>
            <FaExclamationCircle className="relative z-10 flex items-center justify-center w-6 h-6 text-[#B42A19] bg-white rounded-full ring-2 ring-[#B42A19]/40 transition-transform duration-700 ease-in-out animate-[pulse-scale_1.6s_ease-in-out_infinite" />
          </div>
        </CTooltip>
      </>
    )
  }

  const ReportWarningIcon = () => {
    return (
      <>
        <CTooltip content={`There are missing report. Please input the missing reports`} placement="right-end" color="white">
          <div className="relative flex items-center justify-center w-6 h-6">
            <span className="absolute inline-flex w-full h-full rounded-full bg-[#B42A19]/30 animate-ping"></span>
            <FaExclamationCircle className="relative z-10 flex items-center justify-center w-6 h-6 text-[#B42A19] bg-white rounded-full ring-2 ring-[#B42A19]/40 transition-transform duration-700 ease-in-out animate-[pulse-scale_1.6s_ease-in-out_infinite" />
          </div>
        </CTooltip>
      </>
    )
  }

  const DropdownView = () => {
    const activeTab = accessibleTabs.find(tab => location.pathname === tab.path);
    if (!['estimate', 'scope_of_work'].includes(activeTab?.key)) return null;

    const sowOptions = [
      { label: "Scope of Work", active: 'SOW' },
      { label: "Work Item Description", active: 'Description' },
    ];

    const estimateOptions = [
      { label: "Detail", active: 'Detail' },
      { label: "Summary", active: 'Summary' },
      { label: "Work Item Description", active: 'Description' },
    ];

    const DropdownItem = ({ onClick, children }) => (
      <CDropdownItem onClick={onClick}>
        {children}
      </CDropdownItem>
    );
    const params = new URLSearchParams(location.search);
    const orderBy = params.get('order_by');

    let options, selectedOption;

    if (activeTab?.key === 'estimate') {
      options = estimateOptions;
      selectedOption = orderBy || estimateOptions[0].active;
    }
    if (activeTab?.key === 'scope_of_work') {
      options = sowOptions;
      selectedOption = orderBy || sowOptions[0].active
    }

    const validOption = options.some(opt => opt.active === selectedOption);
    if (!validOption) return null;

    const handleDropdownClick = (option) => {
      if (activeTab) {
        history.push(`${activeTab.path}?order_by=${encodeURIComponent(option.active)}`)
      }
    }

    if (activeTab?.key === 'estimate') {
      return (
        <div className="p-2 ml-3">
          <CDropdown>
            <CDropdownToggle color="aimhi-textlight-neutral" caret>
              {selectedOption} View
            </CDropdownToggle>
            <CDropdownMenu>
              {estimateOptions.map(option => (
                <DropdownItem key={option.label} onClick={() => handleDropdownClick(option)}>
                  {option.label}
                </DropdownItem>
              ))}
            </CDropdownMenu>
          </CDropdown>
        </div>
      )
    }
    if (activeTab?.key === 'scope_of_work') {
      return (
        <div className="p-2 ml-3">
          <CDropdown>
            <CDropdownToggle color="aimhi-textlight-neutral" caret>
              {selectedOption} View
            </CDropdownToggle>
            <CDropdownMenu>
              {sowOptions.map(option => (
                <DropdownItem key={option.label} onClick={() => handleDropdownClick(option)}>
                  {option.label}
                </DropdownItem>
              ))}
            </CDropdownMenu>
          </CDropdown>
        </div>
      )
    }
  }

  const handleTabNavigate = (tab) => {

    trackEvent(`Click to Project - ${tab.label}`, {
      'Source': 'Project',
      'AIMHI Version': 1,
    });

    history.push(tab.path);
  }
  return (
    <div>
      <div className="flex items-center">
        <FaArrowCircleLeft
          className="mr-3 cursor-pointer back_button"
          size={28}
          title="Go Back"
          onClick={() => history.push('/project/list')}
        />

        <div>
          {!isEditInfo ? (
            <div>
              <div className="flex items-center">
                <span className="Title">{projectInfo?.project_name}</span>
                <FaPencilAlt
                  className="ml-2 cursor-pointer"
                  size={20}
                  title="Edit Project Info"
                  onClick={() => {
                    setEditableInfo({ ...projectInfo });
                    setIsEditInfo(true);
                  }}
                />
              </div>
              <div>
                <span className="Title">Project ID : {projectInfo?.project_identifier}</span>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <label className="w-32 font-medium text-aimhi-textlight">Project Name</label>
                <CInput
                  value={editableInfo?.project_name || ''}
                  onChange={(e) => setEditableInfo({ ...editableInfo, project_name: e.target.value })}
                  placeholder="Enter project name"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="w-32 font-medium text-aimhi-textlight">Project ID</label>
                <CInput
                  value={editableInfo?.project_identifier || ''}
                  onChange={(e) => setEditableInfo({ ...editableInfo, project_identifier: e.target.value })}
                  placeholder="Enter project id"
                />
              </div>
              <div className="flex gap-2 mt-2 ml-32">
                <CButton color="aimhi-textlight-neutral" size="sm" onClick={saveProjectInfo}>
                  Save
                </CButton>
                <CButton
                  color="aimhi-textlight-neutral"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditInfo(false)}
                >
                  Cancel
                </CButton>
              </div>
            </div>

          )}
        </div>
      </div>

      <CCol xs="12" style={{ marginTop: '18px' }} className="px-0">
        <div className="flex items-center justify-between">
          <CNav
            variant="tabs"
            className="flex-1 min-w-0 flex items-center overflow-x-auto no-scrollbar flex-nowrap"
          >
            {accessibleTabs.map((tab) => {
              const isActive = location.pathname === tab.path;
              const isEnabled = tabStatus?.[tab.key];
              if (isActive) {
                dispatch(setModule(tab.recommenderKey));
              }
              return (
                <CNavItem key={tab.path} className="flex-1 min-w-[110px] md:min-w-0 shrink-0 md:shrink my-1 md:my-0">
                  <div className="relative w-full">
                    <CNavLink
                      href={isEnabled ? `#${tab.path}` : undefined}
                      onClick={(e) => {
                        if (!isEnabled) {
                          e.preventDefault();
                          return;
                        }
                        if (e.ctrlKey || e.metaKey) return;
                        e.preventDefault();
                        handleTabNavigate(tab);
                      }}
                      onContextMenu={() => {
                        if (isEnabled) {
                          trackEvent(`Click to Project - ${tab.label}`, {
                            'Source': 'Project',
                            'AIMHI Version': 1,
                          });
                        }
                      }}
                      className={`relative min-h-[41px] w-full px-2 flex items-center justify-center border-t border-l border-r text-center cursor-pointer no-underline transition-all duration-200 ease-in-out origin-bottom whitespace-nowrap
                      ${!isEnabled ? 'bg-gray-200 border border-aimhi-powderblue cursor-not-allowed' : isActive ? 'bg-aimhi-textlight-neutral rounded-t-md text-white cursor-pointer' : 'border border-aimhi-powderblue text-aimhi-textlight-neutral bg-white hover:scale-105 cursor-pointer'}`}
                      disabled={!isEnabled}
                      title={tab.label}
                    >
                      {tab.label}
                    </CNavLink>
                    {/* tabWarnings?.overview will be change to tabWarnings?.scope_of_work */}
                    {tab.key === 'scope_of_work' && tabWarnings?.overview && (
                      <div className="absolute z-10 -top-1 -right-1">
                        <ScopeOfWorkWarningIcon />
                      </div>
                    )}

                    {tab.key === 'estimate' && tabWarnings?.estimate && (
                      <div className="absolute z-10 -top-1 -right-1">
                        <EstimateWarningIcon />
                      </div>
                    )}

                    {tab.key === 'report' && tabWarnings?.report && (
                      <div className="absolute z-10 -top-1 -right-1">
                        <ReportWarningIcon />
                      </div>
                    )}
                  </div>
                </CNavItem>
              );
            })}
          </CNav>
          <DropdownView />
        </div>
      </CCol>
    </div>
  );
};

export default ProjectTabs;
